import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

export const SETTING_ATTRIBUTE = "data-setting-id" as const;
export const SETTING_ALIASES_ATTRIBUTE = "data-setting-aliases" as const;

type NavigationStatus =
  | { status: "idle" }
  | { status: "navigating"; settingId: string }
  | { status: "error"; error: Error }
  | { status: "completed" };

interface SettingNavigationState {
  registerSetting: (id: string, element: HTMLElement) => () => void;
  navigateToSetting: (setting: SettingNavigationItem) => Promise<void>;
  getSettingElement: (id: string) => HTMLElement | undefined;
  getAllSettings: () => readonly SettingNavigationItem[];
  navigationStatus: NavigationStatus;
  highlightDuration: number;
}

export interface SettingNavigationItem {
  id: string;
  path?: string;
}

interface SettingNavigationProviderProps {
  children: React.ReactNode;
  settings?: readonly SettingNavigationItem[];
  highlightDuration?: number;
  onNavigated?: (setting: SettingNavigationItem) => void;
}

const SettingNavigationContext = createContext<
  SettingNavigationState | undefined
>(undefined);

const emptySettings: readonly SettingNavigationItem[] = [];

const missingProviderNavigationState: SettingNavigationState = {
  registerSetting: () => () => undefined,
  async navigateToSetting() {
    return undefined;
  },
  getSettingElement: () => undefined,
  getAllSettings: () => emptySettings,
  navigationStatus: { status: "idle" },
  highlightDuration: 0,
};

function getDecodedHashFragment(hash: string): string {
  const fragment = hash.slice(1);
  if (!fragment) {
    return "";
  }

  try {
    return decodeURIComponent(fragment);
  } catch {
    return fragment;
  }
}

function scrollSettingElementIntoView(settingElement: HTMLElement): void {
  const yOffset = -100;
  const y =
    settingElement.getBoundingClientRect().top + window.scrollY + yOffset;

  window.scrollTo({ top: y, behavior: "smooth" });
}

export const SettingNavigationProvider: React.FC<
  SettingNavigationProviderProps
> = ({
  children,
  settings = emptySettings,
  highlightDuration = 2000,
  onNavigated,
}) => {
  const [availableSettings, setAvailableSettings] = useState<
    readonly SettingNavigationItem[]
  >([]);
  const [navigationStatus, setNavigationStatus] = useState<NavigationStatus>({
    status: "idle",
  });
  // Per-id Set so a setting rendered in multiple mount points (e.g.,
  // the same panel surfaced inside both a slide-out and the main page)
  // doesn't have one mount's cleanup stomp the other's registration.
  const elementsRef = useRef(new Map<string, Set<HTMLElement>>());
  const highlightTimerRef = useRef<NodeJS.Timeout>();
  const currentHighlightRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { teamId } = useParams<{ teamId: string }>();
  const currentSettingRef = useRef<string | null>(null);

  useEffect(() => {
    const fragment = getDecodedHashFragment(routerLocation.hash);
    if (fragment) {
      const setting = settings.find((s) => s.id === fragment);
      requestAnimationFrame(() => {
        if (setting?.path) {
          navigateToSetting(setting).catch((err) =>
            console.error("Failed to navigate to fragment:", err),
          );
          return;
        }

        currentSettingRef.current = fragment;
        setNavigationStatus({ status: "navigating", settingId: fragment });
        waitForElement(fragment)
          .then((element) => {
            scrollSettingElementIntoView(element);
            if (currentSettingRef.current === fragment) {
              currentSettingRef.current = null;
            }
          })
          .catch(() => {
            if (currentSettingRef.current === fragment) {
              currentSettingRef.current = null;
              setNavigationStatus({ status: "idle" });
            }
          });
      });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.hash, settings]);

  useEffect(() => {
    const settingIds = new Set(settings.map((s) => s.id));
    if (settingIds.size !== settings.length) {
      console.warn("Duplicate setting IDs detected in settings prop");
    }
    setAvailableSettings(settings);
  }, [settings]);

  useEffect(() => {
    return () => {
      // FIXME

      if (highlightTimerRef.current) {
        // FIXME
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(highlightTimerRef.current);
      }
      if (currentHighlightRef.current) {
        // FIXME
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const set = elementsRef.current.get(currentHighlightRef.current);
        if (set) {
          for (const element of set) {
            element.removeAttribute("data-highlighted");
          }
        }
      }
      elementsRef.current.clear();
    };
  }, []);

  const registerSetting = useCallback((id: string, element: HTMLElement) => {
    const elements = elementsRef.current;
    let set = elements.get(id);
    if (!set) {
      set = new Set();
      elements.set(id, set);
    }
    set.add(element);
    return () => {
      const current = elements.get(id);
      if (!current) {
        return;
      }
      current.delete(element);
      if (current.size === 0) {
        elements.delete(id);
      }
    };
  }, []);

  const findElementById = useCallback((id: string): HTMLElement | null => {
    const set = elementsRef.current.get(id);
    const first = set ? set.values().next().value : undefined;
    return first ?? findSettingElementInDocument(id);
  }, []);

  const getSettingElement = useCallback(
    (id: string): HTMLElement | undefined => {
      const element = findElementById(id);
      return element || undefined;
    },
    [findElementById],
  );

  const getAllSettings = useCallback((): readonly SettingNavigationItem[] => {
    return availableSettings;
  }, [availableSettings]);

  const waitForElement = useCallback(
    (id: string): Promise<HTMLElement> => {
      return new Promise((resolve, reject) => {
        let frameId: number;

        const check = () => {
          const element = findElementById(id);
          if (element) {
            cleanup();
            resolve(element);
          } else {
            frameId = requestAnimationFrame(check);
          }
        };

        const timeoutId = setTimeout(() => {
          cleanup();
          reject(new Error(`Element with id ${id} not found after 5 seconds`));
        }, 5000);

        const cleanup = () => {
          if (frameId) {
            cancelAnimationFrame(frameId);
          }
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        };

        frameId = requestAnimationFrame(check);
      });
    },
    [findElementById],
  );

  useEffect(() => {
    const handleHighlightComplete = () => {
      setNavigationStatus({ status: "completed" });
    };

    document.addEventListener("highlightComplete", handleHighlightComplete);
    return () => {
      document.removeEventListener(
        "highlightComplete",
        handleHighlightComplete,
      );
    };
  }, []);

  const navigateToSetting = useCallback(
    async (setting: SettingNavigationItem): Promise<void> => {
      if (currentSettingRef.current === setting.id) {
        return;
      }

      try {
        setNavigationStatus({ status: "navigating", settingId: setting.id });
        currentSettingRef.current = setting.id;

        const fullPath =
          setting.path && teamId
            ? `/stores/${teamId}/settings${
                setting.path.startsWith("/") ? setting.path : `/${setting.path}`
              }`
            : undefined;

        if (fullPath) {
          await navigate(fullPath);
        }

        const settingElement = await waitForElement(setting.id);

        scrollSettingElementIntoView(settingElement);

        history.pushState(
          null,
          "",
          `${
            fullPath ?? `${routerLocation.pathname}${routerLocation.search}`
          }#${encodeURIComponent(setting.id)}`,
        );
        if (currentSettingRef.current === setting.id) {
          currentSettingRef.current = null;
        }
        onNavigated?.(setting);
      } catch (error) {
        if (currentSettingRef.current === setting.id) {
          currentSettingRef.current = null;
          setNavigationStatus({
            status: "error",
            error:
              error instanceof Error ? error : new Error("Navigation failed"),
          });
        }
        console.error("Failed to navigate to setting:", error);
      }
    },
    [
      navigate,
      onNavigated,
      routerLocation.pathname,
      routerLocation.search,
      teamId,
      waitForElement,
    ],
  );

  const value = {
    registerSetting,
    navigateToSetting,
    getSettingElement,
    getAllSettings,
    navigationStatus,
    highlightDuration,
  };

  return (
    <SettingNavigationContext.Provider value={value}>
      {children}
    </SettingNavigationContext.Provider>
  );
};

export const useSettingNavigation = () =>
  useContext(SettingNavigationContext) ?? missingProviderNavigationState;

function findSettingElementInDocument(id: string): HTMLElement | null {
  const elements = document.querySelectorAll<HTMLElement>(
    `[${SETTING_ATTRIBUTE}], [${SETTING_ALIASES_ATTRIBUTE}]`,
  );

  return (
    Array.from(elements).find((element) => {
      if (element.getAttribute(SETTING_ATTRIBUTE) === id) {
        return true;
      }

      return getSettingAliases(element).includes(id);
    }) ?? null
  );
}

function getSettingAliases(element: HTMLElement): string[] {
  return (
    element
      .getAttribute(SETTING_ALIASES_ATTRIBUTE)
      ?.split(/\s+/)
      .filter(Boolean) ?? []
  );
}
